import { mapsKey } from '../firebase';

/**
 * Google's Maps JavaScript API, loaded once with the "Routr web" key (allowed only from our sites and 127.0.0.1:5078).
 * Only Google's map ever draws Routr's data: Google's terms keep its content off any other map (CLAUDE.md, "Follow
 * Google Maps terms"). If it can't load, the page says so plainly and draws no map at all.
 */

declare global {
  interface Window {
    gm_authFailure?: () => void;
    google?: typeof google;
  }
}

export type MapsState = { state: 'loading' } | { state: 'ready' } | { state: 'failed'; why: string };

let loading: Promise<void> | null = null;
let authFailed: ((why: string) => void) | null = null;

export function onAuthFailure(f: (why: string) => void) {
  authFailed = f;
}

/** Loads the API once (Google's own bootstrap, as the design page does). */
export function loadMaps(): Promise<void> {
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    if (!mapsKey) return reject(new Error('No map key: set VITE_MAPS_KEY (control-room/README.md).'));
    window.gm_authFailure = () => authFailed?.('Google refused the map key here. It works only from our sites and 127.0.0.1:5078.');
    const params = new URLSearchParams({ key: mapsKey, v: 'weekly', callback: '__routrMaps', loading: 'async' });
    (window as unknown as Record<string, unknown>).__routrMaps = () => resolve();
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    s.async = true;
    s.onerror = () => reject(new Error('Google’s map couldn’t load. Check the connection.'));
    document.head.append(s);
  });
  return loading;
}

/** Google's encoded polyline, decoded (the format the Routes API answers in). */
export { decode } from '../data/polyline';
